import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { Component, StageElement, ViewerDeviceRuntime } from "../../../types";
import { getDefaultViewerDeviceRuntime } from "../../../types";
import { JournalLoader } from "./components/JournalLoader";
import { JournalPluginActivityChrome } from "./components/JournalPluginActivityChrome";
import { getJournalBlockedMessage } from "./getJournalBlockedMessage";
import { JournalErrorBoundary } from "./layout/JournalErrorBoundary";
import { parseJournalPluginConfig } from "./parseJournalPluginConfig";
import type { JournalSchema } from "./types";
import {
  mountWsolContentBackgroundFix,
} from "./wsolContentBackgroundFix";

const STAGE_ELEMENT_SELECTOR = ".spf-rce-element";

type JournalPluginComponentProps = {
  element: StageElement;
  componentConfig: Component;
  getAuthToken?: (callback: (token: string) => void) => void;
  env?: string;
  viewerDevice?: ViewerDeviceRuntime;
};

function safeDomId(element: StageElement): string {
  return String(element.localRef || element.ref || "journal-plugin").replace(
    /[^a-zA-Z0-9_-]/g,
    "-",
  );
}

function hasRichTextSchema(cfg: ReturnType<typeof parseJournalPluginConfig>): boolean {
  return Boolean(
    cfg.activityLabel || cfg.activityTitle || cfg.instructions || cfg.captions || cfg.credit,
  );
}

export const JournalPluginComponent: React.FC<JournalPluginComponentProps> = ({
  element,
  componentConfig,
  getAuthToken,
  env,
  viewerDevice,
}) => {
  const cfg = parseJournalPluginConfig(componentConfig.config);
  const device = viewerDevice ?? getDefaultViewerDeviceRuntime();
  const blockedMessage = getJournalBlockedMessage(device);
  const domId = useMemo(() => safeDomId(element), [element]);
  const targetId = `journal-plugin-target-item-${domId}`;
  const componentId = String(element.localRef || element.ref || componentConfig?.$id || "");
  const itemId = cfg.itemId;
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (blockedMessage) {
      return;
    }

    mountWsolContentBackgroundFix();
    const wsolCssDelayMs = 3000;
    const delayedFixId = window.setTimeout(mountWsolContentBackgroundFix, wsolCssDelayMs);

    return () => {
      window.clearTimeout(delayedFixId);
    };
  }, [blockedMessage]);

  const resizeParentContainer = useCallback(() => {
    const safeHeight = 20;
    const stageEl = loaderRef.current?.closest(STAGE_ELEMENT_SELECTOR);
    if (stageEl && loaderRef.current) {
      (stageEl as HTMLElement).style.height = `${loaderRef.current.offsetHeight + safeHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (blockedMessage) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const el = loaderRef.current;
    if (!el) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        resizeParentContainer();
        timeoutId = null;
      }, 100);
    });
    resizeObserver.observe(el);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      resizeObserver.disconnect();
    };
  }, [blockedMessage, resizeParentContainer]);

  const schema: JournalSchema | null = useMemo(() => {
    if (!hasRichTextSchema(cfg)) {
      return null;
    }
    return {
      activityLabel: cfg.activityLabel,
      activityTitle: cfg.activityTitle,
      instructions: cfg.instructions,
      captions: cfg.captions,
      credit: cfg.credit,
    };
  }, [cfg]);

  if (blockedMessage) {
    return (
      <div className="bronte-journal-plugin journal-unavailable-message" data-id={componentId}>
        {blockedMessage}
      </div>
    );
  }

  const journalLoader = (
    <JournalErrorBoundary name="BrontePluginJournalWsolError">
      <JournalLoader
        itemId={itemId}
        componentId={componentId}
        env={env}
        getAuthToken={getAuthToken}
      />
    </JournalErrorBoundary>
  );

  return (
    <div
      id={targetId}
      ref={loaderRef}
      className="bronte-journal-plugin"
      data-id={componentId}
    >
      {schema ? (
        <JournalPluginActivityChrome
          schema={schema}
          componentId={componentId}
          journalPluginTargetItemElId={targetId}
        >
          {journalLoader}
        </JournalPluginActivityChrome>
      ) : (
        journalLoader
      )}
    </div>
  );
};
